import {effect, inject, Injectable, signal} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {SimSystemExtendedService} from "@services";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {timer} from "rxjs";
import {toSignal} from "@angular/core/rxjs-interop";

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class SimTimeService {
	private localOffset = 0;

	// Injections
	readonly translate = inject(TranslateService);
	readonly #systemService = inject(SimSystemExtendedService);

	// Derived
	readonly #timeFormat = signal<"HH:mm" | "hh:mm a">(this.translate.currentLang === "en" ? "hh:mm a" : "HH:mm");
	readonly #langChange = toSignal(this.translate.onLangChange);

	// Exposed
	public readonly timeFormat = this.#timeFormat.asReadonly();

	constructor() {
		timer(0, 60 * 1000)
			.pipe(untilDestroyed(this))
			.subscribe(async () => {
				this.localOffset = await this.#systemService.getLocalTimeOffset();
			});
	}

	public getTimeFormat(): 12 | 24 {
		return this.translate.currentLang === "en" ? 12 : 24;
	}

	public static addTimeStringToDate(date: Date, time: string): Date {
		// Extract the hours and minutes from the time string
		let [hours, minutes] = time.split(":");
		minutes = minutes.split(" ")[0]; // In case of AM/PM format

		// Handle the 12/24h formats
		if (time.toLowerCase().includes("pm") && +hours < 12) {
			hours = (+hours + 12).toString();
		} else if (time.toLowerCase().includes("am") && +hours === 12) {
			hours = "00";
		}

		// Create a date object in UTC with the date and time
		return new Date(date.getFullYear(), date.getMonth(), date.getDate(), +hours, +minutes);
	}

	public getLocalTimeString(date: Date): string {
		// Get the current language
		const lang = this.translate.currentLang;

		// Format the time string based on the current language
		const timeFormat = lang === "en" ? "2-digit" : "numeric";

		// Create a formatter
		const formatter = new Intl.DateTimeFormat(lang, {
			hour: timeFormat,
			minute: timeFormat,
			hour12: lang === "en",
			timeZone: "UTC",
		});

		// Format the UTC date to the local timezone
		return formatter.format(date);
	}

	/**
	 * Returns localTime adjusted to the offset of the server and client
	 * This is a cached value and will not trigger an API call
	 * refreshed every 1 minutes
	 */
	public getAdjustedLocalTime(localTime: Date | string | undefined) {
		if (!localTime) {
			return null;
		}
		const ret = new Date(localTime);
		ret.setMilliseconds(ret.getMilliseconds() - this.localOffset);
		return ret;
	}

	readonly #timeFormatChange = effect(() => {
		const langChange = this.#langChange();
		if (langChange?.lang) {
			this.#timeFormat.set(langChange?.lang === "en" ? "hh:mm a" : "HH:mm");
		}
	});

	/**
	 * Converts seconds to milliseconds.
	 * @param {number} seconds - The number of seconds.
	 * @returns {number} The equivalent time in milliseconds.
	 */
	public static fromSecondsToMilliseconds(seconds: number): number {
		return seconds * 1000;
	}

	/**
	 * Converts milliseconds to seconds.
	 * @param {number} milliseconds - The number of milliseconds.
	 * @returns {number} The equivalent time in seconds.
	 */
	public static fromMillisecondsToSeconds(milliseconds: number): number {
		return milliseconds / 1000;
	}

	/**
	 * Converts minutes to milliseconds.
	 * @param {number} minutes - The number of minutes.
	 * @returns {number} The equivalent time in milliseconds.
	 */
	public static fromMinutesToMilliseconds(minutes: number): number {
		return minutes * 60000;
	}

	/**
	 * Converts milliseconds to minutes.
	 * @param {number} milliseconds - The number of milliseconds.
	 * @returns {number} The equivalent time in minutes.
	 */
	public static fromMillisecondsToMinutes(milliseconds: number): number {
		return milliseconds / 60000;
	}
}
